import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  deactivateMember,
  getMember,
  getMyProfile,
  inviteMember,
  listMembers,
  listPlots,
  resendMemberInvite,
  updateMember,
  type ListMembersParams,
  type UpdateMemberInput,
} from '@/lib/members'

export function useMembersList(params: ListMembersParams) {
  return useQuery({
    queryKey: ['members', 'list', params],
    queryFn: () => listMembers(params),
    placeholderData: keepPreviousData,
  })
}

export function useMember(id: string | undefined) {
  return useQuery({
    queryKey: ['members', id],
    queryFn: () => getMember(id!),
    enabled: !!id,
  })
}

export function useMyProfile() {
  return useQuery({
    queryKey: ['members', 'me'],
    queryFn: getMyProfile,
  })
}

export function usePlots(enabled = true) {
  return useQuery({
    queryKey: ['plots'],
    queryFn: listPlots,
    enabled,
  })
}

export function useUpdateMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateMemberInput }) => updateMember(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['members', id] })
      queryClient.invalidateQueries({ queryKey: ['members', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['auth'] })
    },
  })
}

export function useDeactivateMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => deactivateMember(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['approvals'] })
    },
  })
}

export function useInviteMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: inviteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

export function useResendInvite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resendMemberInvite,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['members', id] })
    },
  })
}
